import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import isEmpty from 'lodash/isEmpty';

import Header from '../../../components/Header';
import './styles.css';
import { getDollarFormattedText } from '../../../utils/strings';

const messages = defineMessages({
  title: {
    id: 'hubs.student-life.housing-info.title',
    defaultMessage: 'Housing Information',
  },
  capacity: {
    id: 'hubs.student-life.housing-info.capacity',
    defaultMessage: 'Capacity',
  },
  livingOnCampus: {
    id: 'hubs.student-life.housing-info.living-on-campus',
    defaultMessage: 'Percent living on campus',
  },
  freshmenLiving: {
    id: 'hubs.student-life.housing-info.freshmen-living',
    defaultMessage: 'Freshmen must live on campus',
  },
  sophomoresLiving: {
    id: 'hubs.student-life.housing-info.sophomores-living',
    defaultMessage: 'Sophomores must live on campus',
  },
  juniorsLiving: {
    id: 'hubs.student-life.housing-info.juniors-living',
    defaultMessage: 'Juniors must live on campus',
  },
  seniorsLiving: {
    id: 'hubs.student-life.housing-info.seniors-living',
    defaultMessage: 'Seniors must live on campus',
  },
  footer: {
    id: 'hubs.student-life.housing-info.footer',
    defaultMessage: 'Latest available data from',
  },
});

const getYesNoUnknownValue = value => {
  if (value === true) {
    return 'Yes';
  } else if (value === false) {
    return 'No';
  }

  return '-';
};

const isValidData = housingData => {
  if (isEmpty(housingData)) {
    return false;
  }
  const totalCapacity = get(housingData, 'campusHousingCapacity.value');
  const percOnCampus = get(housingData, 'liveOnCampus.value');

  let freshmenLiving = get(housingData, 'freshmanMustLiveOnCampus.value');
  freshmenLiving = freshmenLiving === true || freshmenLiving === false;
  let sophomoresLiving = get(housingData, 'sophomoresMustLiveOnCampus.value');
  sophomoresLiving = sophomoresLiving === true || sophomoresLiving === false;
  let juniorsLiving = get(housingData, 'juniorsMustLiveOnCampus.value');
  juniorsLiving = juniorsLiving === true || juniorsLiving === false;
  let seniorsLiving = get(housingData, 'seniorsMustLiveOnCampus.value');
  seniorsLiving = seniorsLiving === true || seniorsLiving === false;

  return (
    !!totalCapacity ||
    !!percOnCampus ||
    !!freshmenLiving ||
    !!sophomoresLiving ||
    !!juniorsLiving ||
    !!seniorsLiving
  );
};

const HousingInfo = ({ housingData, lastUpdatedYear, intl }) => {
  const totalCapacity = getDollarFormattedText(
    get(housingData, 'campusHousingCapacity.value', ''),
  );
  const percOnCampus = get(housingData, 'liveOnCampus.value', '');
  const freshmenLiving = get(housingData, 'freshmanMustLiveOnCampus.value');
  const sophomoresLiving = get(housingData, 'sophomoresMustLiveOnCampus.value');
  const juniorsLiving = get(housingData, 'juniorsMustLiveOnCampus.value');
  const seniorsLiving = get(housingData, 'seniorsMustLiveOnCampus.value');

  return isValidData(housingData) ? (
    <div
      className="common-full-width housing-info-container"
      name="housing-information"
      data-cy="housing-info-container"
    >
      <div className="common-core-width">
        <Header as="h2" className="housing-info-title" tabIndex={0}>
          <FormattedMessage {...messages.title} />
        </Header>
        <hr className="horz-line" />
        <ul className="housing-info-content">
          <li tabIndex={0}>
            <FormattedMessage {...messages.capacity} />:
            <div className="value">{totalCapacity ? totalCapacity : '-'}</div>
          </li>
          <li tabIndex={0}>
            <FormattedMessage {...messages.livingOnCampus} />:
            <div className="value">{percOnCampus ? `${percOnCampus}%` : '-'}</div>
          </li>
          <li tabIndex={0}>
            <FormattedMessage {...messages.freshmenLiving} />:
            <div className="value">{getYesNoUnknownValue(freshmenLiving)}</div>
          </li>
          <li tabIndex={0}>
            <FormattedMessage {...messages.sophomoresLiving} />:
            <div className="value">{getYesNoUnknownValue(sophomoresLiving)}</div>
          </li>
          <li tabIndex={0}>
            <FormattedMessage {...messages.juniorsLiving} />:
            <div className="value">{getYesNoUnknownValue(juniorsLiving)}</div>
          </li>
          <li tabIndex={0}>
            <FormattedMessage {...messages.seniorsLiving} />:
            <div className="value">{getYesNoUnknownValue(seniorsLiving)}</div>
          </li>
        </ul>
        {lastUpdatedYear && (
          <div className="footer-text" tabIndex={0}>
            <FormattedMessage {...messages.footer} /> {lastUpdatedYear}
          </div>
        )}
      </div>
    </div>
  ) : null;
};

HousingInfo.propTypes = {
  housingData: PropTypes.object,
  lastUpdatedYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  intl: PropTypes.object,
};

export default injectIntl(HousingInfo);
